import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { QUOTE_STATUSES } from "../../services/quotesService";

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysISO(isoDate, days) {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function emptyItem() {
  return { description: "", quantity: 1, unitPrice: 0 };
}

function toNumber(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0
  );
}

// Form creazione/modifica preventivo. Non scrive su Firestore direttamente: al submit
// chiama onSave(payload, status) lasciando a QuotesPage la scelta tra createQuote /
// updateQuote (di proprietà di questo stesso modulo, ma la chiamata è nel parent per
// coerenza con gli altri form della webapp).
export default function QuoteForm({
  quote = null,
  onClose,
  onSave,
  darkMode = false,
  clients = [],
  nextNumber = "",
}) {
  const isEdit = Boolean(quote?.id);

  const [number, setNumber] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientId, setClientId] = useState(null);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [items, setItems] = useState([emptyItem()]);
  const [tax, setTax] = useState(0);
  const [issueDate, setIssueDate] = useState(todayISO());
  const [validUntil, setValidUntil] = useState(addDaysISO(todayISO(), 30));
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("draft");

  useEffect(() => {
    if (quote) {
      setNumber(quote.number || "");
      setClientName(quote.clientName || "");
      setClientId(quote.clientId || null);
      setItems(
        Array.isArray(quote.items) && quote.items.length > 0
          ? quote.items.map((it) => ({
              description: it.description || "",
              quantity: it.quantity ?? 1,
              unitPrice: it.unitPrice ?? 0,
            }))
          : [emptyItem()]
      );
      setTax(quote.tax || 0);
      setIssueDate(quote.issueDate || todayISO());
      setValidUntil(quote.validUntil || addDaysISO(quote.issueDate || todayISO(), 30));
      setNotes(quote.notes || "");
      setStatus(quote.status || "draft");
    } else {
      setNumber(nextNumber || "");
      setClientName("");
      setClientId(null);
      setItems([emptyItem()]);
      setTax(0);
      setIssueDate(todayISO());
      setValidUntil(addDaysISO(todayISO(), 30));
      setNotes("");
      setStatus("draft");
    }
  }, [quote, nextNumber]);

  const itemsWithTotal = items.map((it) => ({
    ...it,
    total: Math.round(toNumber(it.quantity) * toNumber(it.unitPrice) * 100) / 100,
  }));
  const subtotal = Math.round(itemsWithTotal.reduce((sum, it) => sum + it.total, 0) * 100) / 100;
  const total = Math.round((subtotal + toNumber(tax)) * 100) / 100;

  const updateItem = (index, patch) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (index) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const buildPayload = () => ({
    number: number.trim(),
    clientId,
    clientName: clientName.trim(),
    items: itemsWithTotal,
    subtotal,
    tax: toNumber(tax),
    total,
    issueDate,
    validUntil,
    notes: notes.trim(),
  });

  const handleSubmit = (e, targetStatus) => {
    e.preventDefault();
    if (!clientName.trim()) {
      alert("Seleziona o inserisci un cliente.");
      return;
    }
    const hasValidItem = itemsWithTotal.some((it) => it.description.trim() && it.total > 0);
    if (!hasValidItem) {
      alert("Inserisci almeno una voce con descrizione e importo valido.");
      return;
    }
    onSave(buildPayload(), targetStatus || status);
  };

  const inputClass = `mt-1 w-full px-3 py-2 border rounded-lg text-sm ${
    darkMode
      ? "border-neutral-700 bg-neutral-800 text-white"
      : "border-neutral-200 bg-white text-neutral-900"
  }`;
  const labelClass = `text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div
        className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl border p-5 ${
          darkMode ? "bg-neutral-900 border-neutral-800" : "bg-white border-neutral-200"
        }`}
      >
        <div className="flex items-start justify-between">
          <h3 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            {isEdit ? "Modifica preventivo" : "Nuovo preventivo"}
          </h3>
          <button
            className={`p-2 rounded-lg ${
              darkMode ? "hover:bg-neutral-800 text-neutral-500" : "hover:bg-neutral-100 text-neutral-500"
            }`}
            onClick={onClose}
            aria-label="Chiudi"
            title="Chiudi"
          >
            ✕
          </button>
        </div>

        <form onSubmit={(e) => handleSubmit(e)} className="mt-4 space-y-4">
          {/* Numero + date */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Numero preventivo</label>
              <input className={inputClass} value={number} onChange={(e) => setNumber(e.target.value)} required />
            </div>
            <div>
              <label className={labelClass}>Data emissione</label>
              <input
                type="date"
                className={inputClass}
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Valido fino al</label>
              <input
                type="date"
                className={inputClass}
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                min={issueDate}
              />
            </div>
          </div>

          {/* Cliente */}
          <div className="relative">
            <label className={labelClass}>Cliente</label>
            <input
              className={inputClass}
              value={clientName}
              onChange={(e) => {
                setClientName(e.target.value);
                setClientId(null);
                setShowClientSuggestions(true);
              }}
              onFocus={() => setShowClientSuggestions(true)}
              onBlur={() => setTimeout(() => setShowClientSuggestions(false), 150)}
              placeholder="Es. ACME Srl"
              autoComplete="off"
              required
            />
            {showClientSuggestions && clients.length > 0 && (
              <div
                className={`absolute z-50 mt-1 w-full border rounded-lg shadow-lg max-h-48 overflow-y-auto ${
                  darkMode ? "bg-neutral-800 border-neutral-700" : "bg-white border-neutral-300"
                }`}
              >
                {clients
                  .filter((c) => (c.name || "").toLowerCase().includes((clientName || "").toLowerCase()))
                  .slice(0, 8)
                  .map((c) => (
                    <div
                      key={c.id}
                      className={`px-3 py-2 text-sm cursor-pointer ${
                        darkMode ? "text-white hover:bg-neutral-700" : "text-neutral-900 hover:bg-neutral-100"
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setClientName(c.name);
                        setClientId(c.id);
                        setShowClientSuggestions(false);
                      }}
                    >
                      {c.name}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Voci */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelClass}>Voci</label>
              <button
                type="button"
                onClick={addItem}
                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${
                  darkMode ? "text-forfy-300 hover:bg-neutral-800" : "text-forfy-600 hover:bg-forfy-50"
                }`}
              >
                <Plus className="h-3 w-3" />
                Aggiungi voce
              </button>
            </div>

            <div className="space-y-2">
              {items.map((it, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    className={`${inputClass} col-span-6 mt-0`}
                    placeholder="Descrizione"
                    value={it.description}
                    onChange={(e) => updateItem(index, { description: e.target.value })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`${inputClass} col-span-2 mt-0`}
                    placeholder="Qtà"
                    value={it.quantity}
                    onChange={(e) => updateItem(index, { quantity: e.target.value })}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={`${inputClass} col-span-3 mt-0`}
                    placeholder="Prezzo unit. (€)"
                    value={it.unitPrice}
                    onChange={(e) => updateItem(index, { unitPrice: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    disabled={items.length === 1}
                    className={`col-span-1 flex items-center justify-center p-2 rounded-lg ${
                      items.length === 1
                        ? "opacity-30 cursor-not-allowed"
                        : darkMode
                          ? "text-red-400 hover:bg-red-900/20"
                          : "text-red-500 hover:bg-red-50"
                    }`}
                    aria-label="Rimuovi voce"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <div className={`col-span-12 text-right text-xs ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                    Totale riga: {formatCurrency(toNumber(it.quantity) * toNumber(it.unitPrice))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totali */}
          <div className={`rounded-lg border p-3 ${darkMode ? "border-neutral-800 bg-neutral-800/50" : "border-neutral-200 bg-neutral-50"}`}>
            <div className="flex items-center justify-between text-sm">
              <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>Subtotale</span>
              <span className={darkMode ? "text-white" : "text-neutral-900"}>{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-sm mt-2 gap-3">
              <span className={darkMode ? "text-neutral-400" : "text-neutral-600"}>Imposta / ritenuta (€)</span>
              <input
                type="number"
                step="0.01"
                className={`w-28 px-2 py-1 border rounded-lg text-sm text-right ${
                  darkMode ? "border-neutral-700 bg-neutral-800 text-white" : "border-neutral-200 bg-white text-neutral-900"
                }`}
                value={tax}
                onChange={(e) => setTax(e.target.value)}
              />
            </div>
            <div className={`flex items-center justify-between text-sm font-semibold mt-2 pt-2 border-t ${darkMode ? "border-neutral-700 text-white" : "border-neutral-200 text-neutral-900"}`}>
              <span>Totale</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Status (solo in modifica, per gestire accettato/rifiutato/scaduto) */}
          {isEdit && (
            <div>
              <label className={labelClass}>Stato</label>
              <select
                className={inputClass}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {QUOTE_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Note */}
          <div>
            <label className={labelClass}>Note (opzionale)</label>
            <textarea
              className={`${inputClass} min-h-[72px]`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Condizioni, termini di pagamento, note interne..."
            />
          </div>

          {/* Azioni */}
          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <button
              type="button"
              className={`px-3 py-2 rounded-lg border text-sm ${
                darkMode
                  ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
              onClick={onClose}
            >
              Annulla
            </button>
            {isEdit ? (
              <button
                type="submit"
                className="px-3 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
              >
                Salva
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, "draft")}
                  className={`px-3 py-2 rounded-lg border text-sm ${
                    darkMode
                      ? "border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                      : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                >
                  Salva bozza
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, "sent")}
                  className="px-3 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600"
                >
                  Invia al cliente
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
